import Vue from 'vue';
import axios, { AxiosRequestConfig } from 'axios';
import VueAxios from 'vue-axios';
import { API_URL } from '@/config/config';
import menuModule from '@/store/modules/common/menu.module';
import messageModule from '@/store/modules/common/message.module';
import { MessageType } from '@/config/enum';
import jwtService from '@/services/common/jwt.service';
import { StatusCodes } from 'http-status-codes';
import authModule from '@/store/modules/common/auth.module';

export class HttpService {
  private request: number = 0;
  private response: number = 0;

  constructor() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = API_URL;
    this.initInterceptor();
  }

  public initInterceptor() {
    this.request = Vue.axios.interceptors.request.use(async config => {
      if (jwtService.getToken()) {
        this.updateToken(config, jwtService.getToken());
      } else {
        await authModule.logout();
      }
      await menuModule.updateLoading(true);
      return config;
    });
    this.response = Vue.axios.interceptors.response.use(
      async response => {
        await menuModule.updateLoading(false);
        return response;
      },
      async error => {
        if (
          error.response.data.code === StatusCodes.UNAUTHORIZED ||
          error.response.data.code === StatusCodes.FORBIDDEN
        ) {
          await authModule.logout();
          window.location.href = '/';
        } else {
          await Promise.all([
            menuModule.updateLoading(false),
            messageModule.showMessage({
              message: error.response.data.message,
              type: MessageType.danger
            })
          ]);
        }
        throw error;
      }
    );
  }

  public updateToken(config: AxiosRequestConfig, token: string | null): void {
    config.headers.common['Authorization'] = `${token}`;
  }

  public async query(resource: string, params?: any): Promise<any> {
    return await Vue.axios.get(resource, { params });
  }

  public async get(resource: string, slug?: string): Promise<any> {
    return await Vue.axios.get(`${resource}/${slug}`);
  }

  public async post(resource: string, params?: any): Promise<any> {
    return await Vue.axios.post(`${resource}`, params);
  }

  public async patch(resource: string, params?: any): Promise<any> {
    return await Vue.axios.patch(`${resource}`, params);
  }

  public async postWithFormData(
    resource: string,
    formData?: FormData,
    header?: any
  ) {
    return await Vue.axios.post(`${resource}`, formData, header);
  }

  public async update(
    resource: string,
    slug?: string,
    params?: any
  ): Promise<any> {
    return await Vue.axios.put(`${resource}/${slug}`, params);
  }

  public async put(resource: string, params?: any): Promise<any> {
    return await Vue.axios.put(`${resource}`, params);
  }

  public async delete(resource: string): Promise<any> {
    return await Vue.axios.delete(resource);
  }
}

const $http = new HttpService();
export default $http;
